import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

/* ═══════════════════════════════════════════════════════════════════
   Enrichment V3 — Pipeline Inspector
   Shows the data flow: Extraction → Routing → Enrichment → Catalog
   ═══════════════════════════════════════════════════════════════════ */

const fmt = (v) => {
  if (v == null) return '--';
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const STEPS = [
  { id: 'extraction', num: 1, label: 'Extraction',  desc: 'Raw data pulled from the PDF' },
  { id: 'routing',    num: 2, label: 'Routing',      desc: 'Bill & line items created' },
  { id: 'enrichment', num: 3, label: 'Enrichment',   desc: 'LLM identity resolution & pricing' },
  { id: 'catalog',    num: 4, label: 'Catalog',      desc: 'Vendor product catalog entries' },
];

// ─── Main component ───

const EnrichmentV3View = ({ loaded }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [activeStep, setActiveStep] = useState('extraction');
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Load document list
  useEffect(() => {
    apiFetch('/api/documents')
      .then(async (res) => {
        const d = await res.json();
        const docs = (d.documents || []).filter((doc) => doc.processing_status === 'complete');
        setDocuments(docs);
        if (docs.length > 0) setSelectedDocId(docs[0].id);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Load pipeline for selected doc
  useEffect(() => {
    if (!selectedDocId) return;
    setPipelineLoading(true);
    setPipeline(null);
    apiFetch(`/api/documents/${selectedDocId}/pipeline`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load pipeline');
        return res.json();
      })
      .then(setPipeline)
      .catch((e) => setError(e.message))
      .finally(() => setPipelineLoading(false));
  }, [selectedDocId]);

  const selectedDoc = documents.find((d) => d.id === selectedDocId);

  // Load PDF view URL
  const openPdf = async () => {
    if (pdfOpen) { setPdfOpen(false); return; }
    if (!selectedDocId) return;
    setPdfLoading(true);
    try {
      const res = await apiFetch(`/api/documents/${selectedDocId}/view-url`);
      if (!res.ok) throw new Error('Failed to get PDF URL');
      const { url } = await res.json();
      setPdfUrl(url);
      setPdfOpen(true);
    } catch (e) {
      console.error('PDF load error:', e);
    } finally {
      setPdfLoading(false);
    }
  };

  // Reset PDF when doc changes
  useEffect(() => { setPdfOpen(false); setPdfUrl(null); }, [selectedDocId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-zinc-400">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" /></svg>
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm text-zinc-400">{error}</p>
      </div>
    );
  }

  return (
    <div className={`transition-all duration-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
      {/* Title + document selector + view button */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900 tracking-tight">Pipeline Inspector</h1>
          <p className="text-xs text-zinc-400 mt-0.5">Trace data through extraction, routing, enrichment, and catalog</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedDocId || ''}
            onChange={(e) => { setSelectedDocId(e.target.value); setActiveStep('extraction'); }}
            className="px-3 py-1.5 text-sm border border-zinc-200 rounded-lg bg-white focus:outline-none focus:border-zinc-400 max-w-xs truncate"
          >
            {documents.length === 0 && <option value="">No processed documents</option>}
            {documents.map((doc) => (
              <option key={doc.id} value={doc.id}>
                {doc.extracted_data?.header?.vendor_name || doc.file_name} — {doc.extracted_data?.header?.document_number || doc.id.slice(0, 8)}
              </option>
            ))}
          </select>
          {selectedDocId && (
            <button
              onClick={openPdf}
              disabled={pdfLoading}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                pdfOpen
                  ? 'bg-zinc-900 text-white border-zinc-900'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400 hover:text-zinc-800'
              }`}
            >
              {pdfLoading ? (
                <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" /></svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 2h7l3 3v7a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" />
                  <path d="M9 2v3h3" />
                  <path d="M5 8h4M5 10h2" />
                </svg>
              )}
              {pdfOpen ? 'Hide PDF' : 'View PDF'}
            </button>
          )}
        </div>
      </div>

      {/* Main layout: pipeline + optional PDF viewer */}
      <div className={`flex gap-4 ${pdfOpen ? '' : ''}`}>
        {/* Pipeline column */}
        <div className={`min-w-0 transition-all duration-300 ${pdfOpen ? 'flex-1' : 'w-full'}`}>
          {/* Step header bar */}
          <div className="flex items-stretch bg-zinc-50 border border-zinc-200 rounded-xl overflow-hidden mb-6">
            {STEPS.map((step, i) => {
              const isActive = activeStep === step.id;
              const hasData = stepHasData(step.id, pipeline);
              return (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`flex-1 relative px-4 py-3 text-left transition-all ${
                    isActive
                      ? 'bg-white shadow-sm z-10'
                      : 'hover:bg-zinc-100/80'
                  } ${i > 0 ? 'border-l border-zinc-200' : ''}`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold ${
                      isActive
                        ? 'bg-zinc-900 text-white'
                        : hasData
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-zinc-200 text-zinc-400'
                    }`}>
                      {hasData && !isActive ? (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M2 6l3 3 5-5" /></svg>
                      ) : step.num}
                    </span>
                    <div className="min-w-0">
                      <p className={`text-xs font-medium truncate ${isActive ? 'text-zinc-900' : 'text-zinc-500'}`}>{step.label}</p>
                      {!pdfOpen && <p className="text-[10px] text-zinc-400 truncate hidden sm:block">{step.desc}</p>}
                    </div>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 text-zinc-300">
                      <svg width="8" height="12" viewBox="0 0 8 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M1 1l5 5-5 5" /></svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Step content */}
          {pipelineLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="flex items-center gap-3 text-zinc-400">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" /></svg>
                <span className="text-sm">Loading pipeline...</span>
              </div>
            </div>
          ) : pipeline ? (
            <div className="min-h-[300px]">
              {activeStep === 'extraction' && <ExtractionStep pipeline={pipeline} />}
              {activeStep === 'routing' && <RoutingStep pipeline={pipeline} />}
              {activeStep === 'enrichment' && <EnrichmentStep pipeline={pipeline} />}
              {activeStep === 'catalog' && <CatalogStep pipeline={pipeline} />}
            </div>
          ) : (
            <p className="text-sm text-zinc-400 text-center py-12">Select a document above</p>
          )}
        </div>

        {/* PDF viewer panel */}
        {pdfOpen && pdfUrl && (
          <div className="w-[480px] shrink-0 flex flex-col border border-zinc-200 rounded-xl overflow-hidden bg-white sticky top-0 self-start" style={{ height: 'calc(100vh - 140px)' }}>
            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100 bg-zinc-50/50">
              <span className="text-xs font-medium text-zinc-600">
                {selectedDoc?.file_name || 'Document'}
              </span>
              <button onClick={() => setPdfOpen(false)} className="text-zinc-400 hover:text-zinc-600 transition-colors">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M4 4l6 6M10 4l-6 6" /></svg>
              </button>
            </div>
            <iframe
              src={pdfUrl}
              title="Document PDF"
              className="flex-1 w-full"
              style={{ border: 'none' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Helpers ───

function stepHasData(stepId, pipeline) {
  if (!pipeline) return false;
  switch (stepId) {
    case 'extraction': return !!pipeline.document?.raw_extraction;
    case 'routing': return !!pipeline.bill;
    case 'enrichment': return pipeline.enriched_items?.length > 0;
    case 'catalog': return pipeline.enriched_items?.some((i) => i.enrichment?.identity?.required?.canonical_name);
    default: return false;
  }
}

// ─── Step 1: Extraction ───

const ExtractionStep = ({ pipeline }) => {
  const doc = pipeline.document;
  const raw = doc?.raw_extraction;
  const classification = doc?.classification;

  if (!raw) {
    return <EmptyStep message="No extraction data — document may still be processing" />;
  }

  const header = raw.header || {};
  const lineItems = raw.line_items || [];

  return (
    <div className="space-y-4">
      {/* Classification */}
      {classification && (
        <Card title="Classification" badge={`${(classification.confidence * 100).toFixed(0)}% confidence`} badgeColor={classification.confidence >= 0.8 ? 'emerald' : 'amber'}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Direction" value={classification.direction} />
            <Field label="Type" value={classification.document_type} />
            <Field label="Sender" value={classification.sender_name} />
            <Field label="Recipient" value={classification.recipient_name} />
          </div>
          {classification.reasoning && (
            <p className="text-[11px] text-zinc-400 mt-2 italic">{classification.reasoning}</p>
          )}
        </Card>
      )}

      {/* Extracted header */}
      <Card title="Extracted Header" badge={`${Object.keys(header).length} fields`}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(header).map(([k, v]) => (
            <Field key={k} label={k.replace(/_/g, ' ')} value={v} />
          ))}
        </div>
      </Card>

      {/* Extracted line items */}
      <Card title="Extracted Line Items" badge={`${lineItems.length} items`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-100 text-[10px] uppercase tracking-wider text-zinc-400">
                <th className="text-left py-1.5 pr-3 font-medium">#</th>
                <th className="text-left py-1.5 pr-3 font-medium">Description</th>
                <th className="text-left py-1.5 pr-3 font-medium">Item #</th>
                <th className="text-right py-1.5 pr-3 font-medium">Qty</th>
                <th className="text-left py-1.5 pr-3 font-medium">UOM</th>
                <th className="text-right py-1.5 pr-3 font-medium">Unit Price</th>
                <th className="text-right py-1.5 font-medium">Extended</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li, i) => (
                <tr key={i} className="border-b border-zinc-50">
                  <td className="py-1.5 pr-3 text-zinc-400 font-mono">{li.line_number || i + 1}</td>
                  <td className="py-1.5 pr-3 text-zinc-700 max-w-xs truncate">{li.description}</td>
                  <td className="py-1.5 pr-3 text-zinc-400 font-mono">{li.item_number || '--'}</td>
                  <td className="py-1.5 pr-3 text-right text-zinc-600 font-mono">{li.quantity_ordered ?? li.quantity ?? '--'}</td>
                  <td className="py-1.5 pr-3 text-zinc-500">{li.unit_of_measure || '--'}</td>
                  <td className="py-1.5 pr-3 text-right text-zinc-600 font-mono">{fmt(li.unit_price)}</td>
                  <td className="py-1.5 text-right text-zinc-700 font-mono font-medium">{fmt(li.extended_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Raw JSON collapsible */}
      <CollapsibleJSON title="Full Raw Extraction JSON" data={raw} />
    </div>
  );
};

// ─── Step 2: Routing ───

const RoutingStep = ({ pipeline }) => {
  const bill = pipeline.bill;
  const lineItems = pipeline.line_items || [];

  if (!bill) {
    return <EmptyStep message="No bill created — document may have been classified as non-invoice" />;
  }

  return (
    <div className="space-y-4">
      <Card title="Bill Record" badge={bill.source_doc_id ? 'Linked to document' : ''}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Bill #" value={bill.bill_number} />
          <Field label="Vendor" value={bill.vendor_name} />
          <Field label="Date" value={bill.bill_date} />
          <Field label="Due Date" value={bill.due_date} />
          <Field label="Total" value={fmt(bill.total_amount)} />
          <Field label="Payment Terms" value={bill.payment_terms} />
          <Field label="Vendor ID" value={bill.vendor_id?.slice(0, 8) + '...'} mono />
          <Field label="Status" value={bill.status} />
        </div>
      </Card>

      <Card title="Routed Line Items" badge={`${lineItems.length} items`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-100 text-[10px] uppercase tracking-wider text-zinc-400">
                <th className="text-left py-1.5 pr-3 font-medium">#</th>
                <th className="text-left py-1.5 pr-3 font-medium">Description</th>
                <th className="text-left py-1.5 pr-3 font-medium">Item #</th>
                <th className="text-right py-1.5 pr-3 font-medium">Qty Ordered</th>
                <th className="text-right py-1.5 pr-3 font-medium">Qty Shipped</th>
                <th className="text-left py-1.5 pr-3 font-medium">UOM</th>
                <th className="text-right py-1.5 pr-3 font-medium">Unit Price</th>
                <th className="text-right py-1.5 font-medium">Extended</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li, i) => (
                <tr key={li.id || i} className="border-b border-zinc-50">
                  <td className="py-1.5 pr-3 text-zinc-400 font-mono">{li.line_number || i + 1}</td>
                  <td className="py-1.5 pr-3 text-zinc-700 max-w-xs truncate">{li.description}</td>
                  <td className="py-1.5 pr-3 text-zinc-400 font-mono">{li.item_number || '--'}</td>
                  <td className="py-1.5 pr-3 text-right text-zinc-600 font-mono">{li.quantity_ordered ?? '--'}</td>
                  <td className="py-1.5 pr-3 text-right text-zinc-600 font-mono">{li.quantity_shipped ?? '--'}</td>
                  <td className="py-1.5 pr-3 text-zinc-500">{li.unit_of_measure || '--'}</td>
                  <td className="py-1.5 pr-3 text-right text-zinc-600 font-mono">{fmt(li.unit_price)}</td>
                  <td className="py-1.5 text-right text-zinc-700 font-mono font-medium">{fmt(li.extended_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ─── Step 3: Enrichment ───

const EnrichmentStep = ({ pipeline }) => {
  const items = pipeline.enriched_items || [];
  const [expandedId, setExpandedId] = useState(null);

  if (items.length === 0) {
    return <EmptyStep message="No enrichment data — items may not have been processed by v3 yet" />;
  }

  return (
    <div className="space-y-2">
      {items.map((item) => {
        const identity = item.enrichment?.identity?.required || {};
        const pricing = item.enrichment?.pricing?.required || {};
        const raw = item.raw || {};
        const isExpanded = expandedId === item.line_item_id;
        const conf = item.enrichment?.identity?.supplementary?._confidence;

        return (
          <div key={item.line_item_id} className="border border-zinc-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setExpandedId(isExpanded ? null : item.line_item_id)}
              className={`w-full px-4 py-3 text-left flex items-center gap-4 transition-colors ${isExpanded ? 'bg-zinc-50' : 'hover:bg-zinc-50'}`}
            >
              {/* Raw → Enriched comparison */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium shrink-0">Raw</span>
                  <span className="text-xs text-zinc-500 truncate">{raw.description || '--'}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] uppercase tracking-wider text-emerald-500 font-medium shrink-0">Resolved</span>
                  <span className="text-sm font-medium text-zinc-800 truncate">{identity.standardized_name || identity.canonical_name?.replace(/_/g, ' ') || '--'}</span>
                  {identity.canonical_name && (
                    <span className="text-[10px] font-mono text-zinc-400 shrink-0">{identity.canonical_name}</span>
                  )}
                </div>
                {identity.category && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-[10px] text-zinc-400">{identity.category}{identity.subcategory ? ` > ${identity.subcategory}` : ''}</span>
                  </div>
                )}
              </div>

              {/* Pricing comparison */}
              <div className="text-right shrink-0 w-40">
                <div className="flex items-center justify-end gap-2 text-xs">
                  <span className="text-zinc-400">{raw.unit_of_measure || '--'} @ {fmt(raw.unit_price)}</span>
                </div>
                <div className="flex items-center justify-end gap-2 text-xs mt-0.5">
                  <span className="text-emerald-600 font-medium">{pricing.unit_normalized || '--'} @ {fmt(pricing.price_per_unit)}</span>
                </div>
              </div>

              {/* Confidence */}
              <div className="shrink-0 w-12 text-right">
                {conf != null && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                    conf >= 0.85 ? 'bg-emerald-100 text-emerald-700' :
                    conf >= 0.7 ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {(conf * 100).toFixed(0)}%
                  </span>
                )}
              </div>

              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                className={`shrink-0 text-zinc-300 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                <path d="M2 4l4 4 4-4" />
              </svg>
            </button>

            {isExpanded && (
              <div className="border-t border-zinc-100 p-4 space-y-3">
                {/* Identity */}
                <Card title="Identity Resolution" compact>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Field label="Standardized Name" value={identity.standardized_name} />
                    <Field label="Canonical Key" value={identity.canonical_name} mono />
                    <Field label="Brand" value={identity.brand} />
                    <Field label="Form" value={identity.form} />
                    <Field label="Grade" value={identity.grade} />
                    <Field label="Vendor SKU" value={identity.vendor_sku} mono />
                    <Field label="Variant" value={identity.variant_descriptor} />
                    <Field label="Category" value={identity.category} />
                    <Field label="Subcategory" value={identity.subcategory} />
                  </div>
                </Card>

                {/* Purchase Summary — LLM's plain-english restatement */}
                {pricing.purchase_summary && (
                  <div className="px-3 py-2.5 bg-amber-50/50 border border-amber-200/60 rounded-lg">
                    <p className="text-[10px] uppercase tracking-wider text-amber-500 font-medium mb-1">Purchase Summary</p>
                    <p className="text-xs text-zinc-700 leading-relaxed">{pricing.purchase_summary}</p>
                  </div>
                )}

                {/* Pricing */}
                <Card title="Pricing Normalization" compact>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Field label="Price Per Unit" value={fmt(pricing.price_per_unit)} />
                    <Field label="Unit Normalized" value={pricing.unit_normalized} />
                    <Field label="Total Line Price" value={fmt(pricing.total_line_price)} />
                    <Field label="Quantity" value={pricing.quantity} />
                    <Field label="Currency" value={pricing.currency} />
                    <Field label="Price Basis" value={pricing.price_basis} />
                    <Field label="Discount" value={pricing.discount_applied != null ? (pricing.discount_applied ? 'Yes' : 'No') : null} />
                    <Field label="Source" value={pricing.price_source} />
                  </div>
                  {pricing.cost_reasoning?.divisor_explanation && (
                    <div className="mt-2 px-1 py-1 bg-zinc-50 rounded text-[10px] text-zinc-500 font-mono">
                      {pricing.cost_reasoning.divisor_explanation}
                    </div>
                  )}
                  {pricing.price_vector && pricing.price_vector.length > 0 && (
                    <div className="mt-3 border-t border-zinc-100 pt-2">
                      <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Price Vector</div>
                      <div className="space-y-1">
                        {pricing.price_vector.map((v, i) => (
                          <div key={i} className="flex justify-between text-xs">
                            <span className="text-zinc-500">{v.label}</span>
                            <span className="font-mono text-zinc-700">${typeof v.price === 'number' ? v.price.toFixed(4) : v.price}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>

                {/* Supplementary data from each brief */}
                {Object.entries(item.enrichment || {}).map(([briefId, briefData]) => {
                  const supp = briefData?.supplementary;
                  if (!supp || Object.keys(supp).filter(k => k !== '_confidence').length === 0) return null;
                  const { _confidence, ...rest } = supp;
                  return (
                    <CollapsibleJSON
                      key={briefId}
                      title={`${briefId.replace(/_/g, ' ')} — supplementary`}
                      data={rest}
                      badge={_confidence != null ? `${(_confidence * 100).toFixed(0)}%` : undefined}
                    />
                  );
                })}

                {/* Meta */}
                {item.enrichment_meta && (
                  <div className="flex items-center gap-4 text-[10px] text-zinc-400 pt-1">
                    <span>Model: {item.enrichment_meta.model}</span>
                    <span>Status: {item.enrichment_meta.status}</span>
                    <span>Processed: {item.enrichment_meta.processed_at?.slice(0, 16)}</span>
                    {item.enrichment_meta.brief_versions && (
                      <span>Briefs: {Object.entries(item.enrichment_meta.brief_versions).map(([k, v]) => `${k}@${v}`).join(', ')}</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Step 4: Catalog ───

const CatalogStep = ({ pipeline }) => {
  const items = pipeline.enriched_items || [];

  // Build catalog view from enriched items
  const catalogEntries = useMemo(() => {
    const map = {};
    for (const item of items) {
      const identity = item.enrichment?.identity?.required;
      const pricing = item.enrichment?.pricing?.required;
      if (!identity?.canonical_name) continue;
      const key = identity.canonical_name;
      if (!map[key]) {
        map[key] = {
          canonical_name: identity.canonical_name,
          brand: identity.brand,
          form: identity.form,
          grade: identity.grade,
          category: identity.category || null,
          subcategory: identity.subcategory || null,
          unit_normalized: pricing?.unit_normalized,
          prices: [],
        };
      }
      if (pricing?.price_per_unit != null) {
        map[key].prices.push(pricing.price_per_unit);
      }
    }
    return Object.values(map).sort((a, b) => (a.category || '').localeCompare(b.category || '') || a.canonical_name.localeCompare(b.canonical_name));
  }, [items]);

  if (catalogEntries.length === 0) {
    return <EmptyStep message="No catalog entries — enrichment may not have produced canonical names" />;
  }

  return (
    <div className="space-y-4">
      <Card title="Products Identified" badge={`${catalogEntries.length} unique products`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-100 text-[10px] uppercase tracking-wider text-zinc-400">
                <th className="text-left py-1.5 pr-3 font-medium">Canonical Name</th>
                <th className="text-left py-1.5 pr-3 font-medium">Category</th>
                <th className="text-left py-1.5 pr-3 font-medium">Brand</th>
                <th className="text-left py-1.5 pr-3 font-medium">Form</th>
                <th className="text-left py-1.5 pr-3 font-medium">Unit</th>
                <th className="text-right py-1.5 pr-3 font-medium">Avg Price/Unit</th>
                <th className="text-right py-1.5 font-medium">Seen</th>
              </tr>
            </thead>
            <tbody>
              {catalogEntries.map((entry) => {
                const avg = entry.prices.length > 0
                  ? entry.prices.reduce((a, b) => a + b, 0) / entry.prices.length
                  : null;
                return (
                  <tr key={entry.canonical_name} className="border-b border-zinc-50">
                    <td className="py-2 pr-3 text-zinc-800 font-medium">{entry.canonical_name.replace(/_/g, ' ')}</td>
                    <td className="py-2 pr-3">
                      {entry.category && (
                        <span className="text-[10px] text-zinc-500">{entry.category}{entry.subcategory ? ` > ${entry.subcategory}` : ''}</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-zinc-500">{entry.brand || '--'}</td>
                    <td className="py-2 pr-3 text-zinc-500">{entry.form || '--'}</td>
                    <td className="py-2 pr-3 text-zinc-500">{entry.unit_normalized || '--'}</td>
                    <td className="py-2 pr-3 text-right font-mono text-zinc-700">{fmt(avg)}</td>
                    <td className="py-2 text-right font-mono text-zinc-400">{entry.prices.length}x</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ─── Shared UI components ───

const Card = ({ title, badge, badgeColor = 'zinc', compact, children }) => (
  <div className={`bg-white border border-zinc-200 rounded-lg overflow-hidden ${compact ? '' : ''}`}>
    <div className={`flex items-center justify-between px-4 ${compact ? 'py-2' : 'py-2.5'} border-b border-zinc-100 bg-zinc-50/50`}>
      <h3 className="text-xs font-semibold text-zinc-700 tracking-wide">{title}</h3>
      {badge && (
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
          badgeColor === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
          badgeColor === 'amber' ? 'bg-amber-100 text-amber-700' :
          'bg-zinc-100 text-zinc-500'
        }`}>
          {badge}
        </span>
      )}
    </div>
    <div className="px-4 py-3">{children}</div>
  </div>
);

const Field = ({ label, value, mono }) => (
  <div className="min-w-0">
    <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium">{label}</p>
    <p className={`text-xs text-zinc-700 mt-0.5 truncate ${mono ? 'font-mono' : ''}`}>
      {value != null && value !== '' ? String(value) : <span className="text-zinc-300">--</span>}
    </p>
  </div>
);

const EmptyStep = ({ message }) => (
  <div className="flex flex-col items-center justify-center h-48 text-center">
    <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center mb-3">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-zinc-400">
        <circle cx="8" cy="8" r="6" />
        <path d="M8 5v3" /><circle cx="8" cy="10.5" r="0.5" fill="currentColor" />
      </svg>
    </div>
    <p className="text-sm text-zinc-400">{message}</p>
  </div>
);

const CollapsibleJSON = ({ title, data, badge }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-2 text-left bg-zinc-50/50 hover:bg-zinc-100/50 transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-zinc-600 capitalize">{title}</span>
          {badge && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">{badge}</span>}
        </div>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
          className={`text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`}>
          <path d="M2 4l4 4 4-4" />
        </svg>
      </button>
      {open && (
        <pre className="px-4 py-3 text-[11px] text-zinc-600 font-mono overflow-x-auto max-h-80 overflow-y-auto whitespace-pre-wrap bg-white">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default EnrichmentV3View;
